const API_BASE = process.env.REACT_APP_GUARDIAN_API_URL || 'http://localhost:3000/api/v1/guardian';

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const body = await res.json();
      if (!res.ok && body.detail && !body.message) {
        const detail = Array.isArray(body.detail)
          ? body.detail.map((item) => item.msg || JSON.stringify(item)).join('; ')
          : String(body.detail);
        return { ...body, success: false, message: detail };
      }
      return body;
    } catch {
      return {
        success: false,
        message: 'Invalid JSON response from server',
      };
    }
  }

  const text = await res.text();
  return {
    success: false,
    message: text.slice(0, 200) || `Request failed with status ${res.status}`,
  };
}

async function guardianFetch(path, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: controller.signal,
    });
    const body = await parseResponse(res);
    if (typeof body.success === 'undefined') {
      return { status: res.status, ok: res.ok, body: { ...body, success: res.ok } };
    }
    return { status: res.status, ok: res.ok, body };
  } catch (err) {
    if (err.name === 'AbortError') {
      return {
        status: 0,
        ok: false,
        body: {
          success: false,
          message:
            'Request timed out. The API may be waiting on a database connection — '
            + 'check Postgres is running on the host in packages/api/.env.',
        },
      };
    }
    return {
      status: 0,
      ok: false,
      body: {
        success: false,
        message:
          err.message === 'Failed to fetch'
            ? `Cannot reach Guardian API at ${API_BASE}. Is the backend running?`
            : (err.message || 'Network request failed'),
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function guardianLogin(email, password, captcha) {
  const { body } = await guardianFetch('/auth/login', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, password, captcha }),
  });
  return body;
}

export async function guardianRegister({
  firstName,
  lastName,
  email,
  password,
  signupToken,
  captcha,
  acceptTermsVersion,
}) {
  const { body } = await guardianFetch('/auth/register', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      firstName,
      lastName,
      email,
      password,
      signupToken,
      captcha,
      // Sent only when the Backend publishes a terms version (audit P-4).
      ...(acceptTermsVersion ? { acceptTermsVersion } : {}),
    }),
  });
  return body;
}

export async function sendGuardianEmailVerificationCode(email, captcha) {
  const { body } = await guardianFetch('/auth/email/send-code', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, captcha }),
  });
  return body;
}

export async function verifyGuardianEmailCode(email, code) {
  const { body } = await guardianFetch('/auth/email/verify', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, code }),
  });
  return body;
}

export async function fetchGuardianMe(token) {
  const { status, body } = await guardianFetch('/me', { headers: authHeaders(token) });
  return { ...body, status };
}

/**
 * Updates the caller's own display name (audit P-18, 2026-10-22). Only
 * firstName/lastName are editable here: email and phone are sign-in identities
 * that need a verification flow (out of scope). The Backend trims, bounds to 64
 * chars and rejects control/format characters; refusals carry data.code
 * (invalid_name | profile_unavailable). On 200 the body's data is the
 * /guardian/me user block.
 * @param {string} token
 * @param {{ firstName?: string, lastName?: string }} changes
 */
export async function updateGuardianProfile(token, changes) {
  const payload = {};
  if (typeof changes.firstName === 'string') payload.firstName = changes.firstName;
  if (typeof changes.lastName === 'string') payload.lastName = changes.lastName;
  const { status, body } = await guardianFetch('/me/profile', {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return { ...body, status };
}

/**
 * Active billing plan catalog for the subscriptions page.
 * @param {string} token
 */
export async function fetchGuardianPlans(token) {
  const { status, body } = await guardianFetch('/plans', { headers: authHeaders(token) });
  return { ...body, status };
}

/**
 * Start Stripe Checkout for a personal plan on the shared BillingAccount.
 * One subscription covers Guardian and the Interlinked platform.
 * @param {string} token
 * @param {{ planSlug: string, period?: 'monthly'|'annual', startTrial?: boolean }} body
 */
export async function createGuardianCheckoutSession(token, body) {
  const { status, body: payload } = await guardianFetch('/checkout-session', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      planSlug: body.planSlug,
      period: body.period || 'monthly',
      startTrial: Boolean(body.startTrial),
    }),
  });
  return { ...payload, status };
}

/**
 * Apply a paid Stripe Checkout session (or the customer's active sub)
 * when the webhook never reached localhost.
 * @param {string} token
 * @param {{ sessionId?: string }} [body]
 */
export async function confirmGuardianCheckoutSession(token, body = {}) {
  const { status, body: payload } = await guardianFetch('/checkout-session/confirm', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      sessionId: body.sessionId || undefined,
    }),
  });
  return { ...payload, status };
}

export async function fetchGuardianSubscription(token) {
  const { status, body } = await guardianFetch('/subscription', {
    headers: authHeaders(token),
  });
  return { ...body, status };
}

export async function cancelGuardianSubscription(token) {
  const { status, body } = await guardianFetch('/subscription/cancel', {
    method: 'POST',
    headers: authHeaders(token),
  });
  return { ...body, status };
}

export async function resumeGuardianSubscription(token) {
  const { status, body } = await guardianFetch('/subscription/resume', {
    method: 'POST',
    headers: authHeaders(token),
  });
  return { ...body, status };
}

export async function fetchGuardianInvoices(token) {
  const { status, body } = await guardianFetch('/invoices', {
    headers: authHeaders(token),
  });
  return { ...body, status };
}

export async function createGuardianPortalSession(token, returnUrl) {
  const { status, body } = await guardianFetch('/portal-session', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ returnUrl }),
  });
  return { ...body, status };
}

export async function reportGuardianIncident(token, body) {
  const result = await guardianFetch('/incidents', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return { status: result.status, body: result.body };
}

export async function fetchGuardianIncident(token, incidentId) {
  const { body } = await guardianFetch(`/incidents/${incidentId}`, {
    headers: authHeaders(token),
  });
  return body;
}

/**
 * The signed-in Guardian user's OWN submitted reports, newest first
 * (plan Track 5 B6, 2026-09-30). `mine=true` is load-bearing: without it
 * GET /guardian/incidents returns the public community feed (other users'
 * reports), not the caller's. The Backend scopes `mine` by the token's user
 * (GuardianIncident.guardianUserId), so the caller is never a parameter here.
 * Body: { success, data: { events: [...], count } }.
 * @param {string} token
 * @param {number} [limit] - 1..100 (Backend `n`, default 20).
 */
export async function fetchMyGuardianIncidents(token, limit = 50) {
  const n = Math.min(100, Math.max(1, Math.floor(Number(limit)) || 50));
  const { status, body } = await guardianFetch(`/incidents?mine=true&n=${n}`, {
    headers: authHeaders(token),
  });
  return { ...body, status };
}

export async function startGuardianTrial(token) {
  const { body } = await guardianFetch('/trial/start', {
    method: 'POST',
    headers: authHeaders(token),
  });
  return body;
}

export async function submitIncidentFeedback(token, incidentId, rating, comment) {
  const { body } = await guardianFetch(`/incidents/${incidentId}/feedback`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ rating, comment }),
  });
  return body;
}

/**
 * Files a bug report (2026-09-30). POST /bug-reports, bearer token, body per the
 * Backend contract { title, description, steps?, recaptcha_token, email?, app,
 * page_url?, user_agent? }. The HTTP status is returned so callers can tell the
 * 429 per-user rate limit (never auto-retried) from a 400/422.
 * @param {string} token
 * @param {object} body
 */
export async function submitBugReport(token, body) {
  const { status, body: payload } = await guardianFetch('/bug-reports', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return { ...payload, status };
}

export async function fetchGuardianAuthMethods() {
  const { body } = await guardianFetch('/auth/methods', { headers: authHeaders() });
  return body;
}

export async function fetchGuardianOAuthUrl(provider, redirectUri) {
  const { body } = await guardianFetch(`/auth/oauth/${provider}/authorize`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ redirect_uri: redirectUri }),
  });
  return body;
}

export async function completeGuardianOAuth(provider, code, redirectUri, state) {
  const { body } = await guardianFetch(`/auth/oauth/${provider}/callback`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ code, redirect_uri: redirectUri, state }),
  });
  return body;
}

export async function sendGuardianPhoneCode(phone, captcha, firstName, lastName) {
  const { body } = await guardianFetch('/auth/phone/send-code', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ phone, captcha, firstName, lastName }),
  });
  return body;
}

export async function verifyGuardianPhoneCode(phone, code, firstName, lastName) {
  const { body } = await guardianFetch('/auth/phone/verify', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ phone, code, firstName, lastName }),
  });
  return body;
}

export async function setGuardianPhonePassword(setupToken, password, confirmPassword) {
  const { body } = await guardianFetch('/auth/phone/set-password', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      setup_token: setupToken,
      password,
      confirm_password: confirmPassword,
    }),
  });
  return body;
}

export async function requestGuardianPasswordReset(email, captcha) {
  const { body } = await guardianFetch('/auth/forgot-password', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, captcha }),
  });
  return body;
}

export async function validateGuardianResetToken(token) {
  const { body } = await guardianFetch(`/auth/validate-reset-token/${encodeURIComponent(token)}`, {
    headers: authHeaders(),
  });
  return body;
}

export async function resetGuardianPassword(token, password, captcha) {
  const { body } = await guardianFetch('/auth/reset-password', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ token, password, captcha }),
  });
  return body;
}

// ---------------------------------------------------------------------------
// Departments (D0, workspace docs/guardian/03 §1.3, 2026-10-21). The Backend
// takes the department from the caller's own membership; nothing here sends
// an agency id. Every call returns the body plus the HTTP status, and refusals
// carry a machine code in data.code (e.g. seat_limit_reached, last_admin).

async function departmentCall(path, token, method = 'GET', payload) {
  const { status, body } = await guardianFetch(path, {
    method,
    headers: authHeaders(token),
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
  });
  return { ...body, status };
}

export function fetchGuardianDepartment(token) {
  return departmentCall('/department', token);
}

export function fetchDepartmentMembers(token) {
  return departmentCall('/department/members', token);
}

export function updateDepartmentMember(token, memberId, changes) {
  return departmentCall(`/department/members/${encodeURIComponent(memberId)}`, token, 'PATCH', changes);
}

export function removeDepartmentMember(token, memberId) {
  return departmentCall(`/department/members/${encodeURIComponent(memberId)}`, token, 'DELETE');
}

export function fetchDepartmentInvites(token) {
  return departmentCall('/department/invites', token);
}

export function createDepartmentInvites(token, emails, role) {
  return departmentCall('/department/invites', token, 'POST', { emails, role });
}

export function revokeDepartmentInvite(token, inviteId) {
  return departmentCall(`/department/invites/${encodeURIComponent(inviteId)}`, token, 'DELETE');
}

export function acceptDepartmentInvite(token, inviteToken) {
  return departmentCall('/department/invites/accept', token, 'POST', { token: inviteToken });
}

export function leaveDepartment(token) {
  return departmentCall('/department/membership', token, 'DELETE');
}

/**
 * Rates one assistant turn (1 helpful, -1 not helpful, 0 clears). The record
 * id is the one the agent sent on that turn's `done` frame.
 */
export function rateGuardianTurn(token, recordId, value) {
  return departmentCall(`/turns/${encodeURIComponent(recordId)}/rating`, token, 'PUT', { value });
}

// ---------------------------------------------------------------------------
// Account lifecycle (audit P-4, 2026-10-21): consent, data export, deletion.

export async function acceptGuardianTerms(token, version) {
  const { status, body } = await guardianFetch('/me/consent', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ version }),
  });
  return { ...body, status };
}

/** Downloads everything Guardian stores about the caller as JSON (60 s budget). */
export async function exportGuardianData(token) {
  const { status, body } = await guardianFetch('/me/export', { headers: authHeaders(token) }, 60000);
  return { ...body, status };
}

export async function deleteGuardianAccount(token) {
  const { status, body } = await guardianFetch('/me', {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify({ confirm: 'DELETE' }),
  }, 60000);
  return { ...body, status };
}

// ---------------------------------------------------------------------------
// Department documents (D1 wave 1, workspace docs/guardian/05, 2026-10-22).
// Admin-only; the Backend scopes everything to the caller's department.

export function createDepartmentDocument(token, { title, kind, bytes, sha256, mime }) {
  return departmentCall('/department/documents', token, 'POST', { title, kind, bytes, sha256, mime });
}

export function finalizeDepartmentDocument(token, id) {
  return departmentCall(`/department/documents/${encodeURIComponent(id)}/finalize`, token, 'POST');
}

export function fetchDepartmentDocuments(token) {
  return departmentCall('/department/documents', token);
}

export function fetchDepartmentDocument(token, id) {
  return departmentCall(`/department/documents/${encodeURIComponent(id)}`, token);
}

export function fetchDepartmentDocumentChunks(token, id, offset = 0, limit = 20) {
  const q = `offset=${Number(offset) | 0}&limit=${Number(limit) | 0}`;
  return departmentCall(`/department/documents/${encodeURIComponent(id)}/chunks?${q}`, token);
}

export function retireDepartmentDocument(token, id) {
  return departmentCall(`/department/documents/${encodeURIComponent(id)}`, token, 'DELETE');
}

export function searchDepartmentDocuments(token, q) {
  return departmentCall('/department/documents/search', token, 'POST', { q });
}

// Department rules (D1 wave 2, workspace docs/guardian/05, 2026-10-22).
// Admin-only; drafts come from the department's own documents.

function ruleQuery({ status, documentId, flagged, conflicted, offset = 0, limit = 50 } = {}) {
  const p = new URLSearchParams();
  if (status) p.set('status', status);
  if (documentId) p.set('documentId', documentId);
  if (flagged) p.set('flagged', 'true');
  if (conflicted) p.set('conflicted', 'true');
  p.set('offset', String(Number(offset) | 0));
  p.set('limit', String(Number(limit) | 0));
  return p.toString();
}

export function fetchDepartmentRules(token, filters) {
  return departmentCall(`/department/rules?${ruleQuery(filters)}`, token);
}

export function fetchDepartmentRule(token, id) {
  return departmentCall(`/department/rules/${encodeURIComponent(id)}`, token);
}

export function addDepartmentRule(token, body) {
  return departmentCall('/department/rules', token, 'POST', body);
}

export function editDepartmentRule(token, id, body) {
  return departmentCall(`/department/rules/${encodeURIComponent(id)}`, token, 'PATCH', body);
}

export function approveDepartmentRule(token, id, rev, acknowledgeFlags = false) {
  return departmentCall(`/department/rules/${encodeURIComponent(id)}/approve`, token, 'POST', { rev, acknowledgeFlags });
}

export function rejectDepartmentRule(token, id, rev) {
  return departmentCall(`/department/rules/${encodeURIComponent(id)}/reject`, token, 'POST', { rev });
}

export function retireDepartmentRule(token, id, rev) {
  return departmentCall(`/department/rules/${encodeURIComponent(id)}/retire`, token, 'POST', { rev });
}

export function fetchDocumentCandidates(token, docId, offset = 0, limit = 50) {
  const q = `offset=${Number(offset) | 0}&limit=${Number(limit) | 0}`;
  return departmentCall(`/department/documents/${encodeURIComponent(docId)}/candidates?${q}`, token);
}

export function fetchDocumentExtraction(token, docId) {
  return departmentCall(`/department/documents/${encodeURIComponent(docId)}/extraction`, token);
}

export function requeueDocumentExtraction(token, docId) {
  return departmentCall(`/department/documents/${encodeURIComponent(docId)}/extract`, token, 'POST');
}

export function publishDepartmentVersion(token) {
  return departmentCall('/department/versions', token, 'POST');
}

export function fetchDepartmentVersions(token) {
  return departmentCall('/department/versions', token);
}

// Department terms / glossary (D2, workspace docs/guardian/03 §3.2, 2026-10-22).
// Members get the approved glossary (read-only); admins manage drafts, import a
// code list, and approve/reject/retire. Everything is scoped by the Backend to
// the caller's department. A 503 data.code === 'terms_unavailable' means the
// feature is switched off for this deployment (the UI hides the section).

function termQuery({ status, q, offset = 0, limit = 50 } = {}) {
  const p = new URLSearchParams();
  if (status) p.set('status', status);
  if (q) p.set('q', q);
  p.set('offset', String(Number(offset) | 0));
  p.set('limit', String(Number(limit) | 0));
  return p.toString();
}

export function fetchDepartmentTerms(token, filters) {
  return departmentCall(`/department/terms?${termQuery(filters)}`, token);
}

export function addDepartmentTerm(token, body) {
  return departmentCall('/department/terms', token, 'POST', body);
}

/** Imports a `code,meaning[,scope]` CSV (≤ 256 KB) as drafts. */
export function importDepartmentTerms(token, csv) {
  return departmentCall('/department/terms/import', token, 'POST', { csv });
}

export function approveDepartmentTerm(token, id, rev) {
  return departmentCall(`/department/terms/${encodeURIComponent(id)}/approve`, token, 'POST', { rev });
}

export function rejectDepartmentTerm(token, id, rev) {
  return departmentCall(`/department/terms/${encodeURIComponent(id)}/reject`, token, 'POST', { rev });
}

export function retireDepartmentTerm(token, id, rev) {
  return departmentCall(`/department/terms/${encodeURIComponent(id)}/retire`, token, 'POST', { rev });
}

// Department structure (D2, §3.2): stations, apparatus, call signs, mutual-aid
// partners, dispatch centers and channel names. Same approval gate as terms.

export function fetchDepartmentStructure(token, status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return departmentCall(`/department/structure${q}`, token);
}

export function addDepartmentStructure(token, body) {
  return departmentCall('/department/structure', token, 'POST', body);
}

export function approveDepartmentStructure(token, id, rev) {
  return departmentCall(`/department/structure/${encodeURIComponent(id)}/approve`, token, 'POST', { rev });
}

export function rejectDepartmentStructure(token, id, rev) {
  return departmentCall(`/department/structure/${encodeURIComponent(id)}/reject`, token, 'POST', { rev });
}

export function retireDepartmentStructure(token, id, rev) {
  return departmentCall(`/department/structure/${encodeURIComponent(id)}/retire`, token, 'POST', { rev });
}

// Department language settings (D2, §3.3): plain-language default and style
// mirroring. GET is any member; PUT is admin.

export function fetchDepartmentSettings(token) {
  return departmentCall('/department/settings', token);
}

export function updateDepartmentSettings(token, changes) {
  return departmentCall('/department/settings', token, 'PUT', changes);
}

// The caller's own learned conversation style (D5, workspace docs/guardian/03
// §3.4). Setting a feature locks it (learning stops for that feature); `unlock`
// lets learning resume. A 503 data.code === 'style_unavailable' hides the
// section.

export function fetchMyStyle(token) {
  return departmentCall('/me/style', token);
}

/**
 * Updates the caller's own style. `features` locks each named feature;
 * `unlock` (an array of feature names) lets learning resume for them.
 * @param {string} token
 * @param {{ enabled?: boolean, features?: object, unlock?: string[] }} changes
 */
export function updateMyStyle(token, changes) {
  return departmentCall('/me/style', token, 'PUT', changes);
}

/** Only an https S3 origin from the Backend's presigned POST is ever used. */
export function isS3UploadUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && /\.s3\.[a-z0-9-]+\.amazonaws\.com$/.test(u.hostname);
  } catch {
    return false;
  }
}

/**
 * Uploads the file with the Backend's presigned POST (fields first, file
 * last, as S3 requires). No credentials, no Guardian token: the policy alone
 * authorizes exactly this key, type and size. 2-minute budget.
 */
export async function uploadToPresignedPost(upload, file, timeoutMs = 120000) {
  if (!upload || !isS3UploadUrl(upload.url) || typeof upload.fields !== 'object') {
    return { ok: false, status: 0 };
  }
  const form = new FormData();
  Object.entries(upload.fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(upload.url, { method: 'POST', body: form, signal: controller.signal, credentials: 'omit' });
    return { ok: res.ok, status: res.status };
  } catch {
    return { ok: false, status: 0 };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Department organization features (WS-F, workspace docs/guardian/06, 2026-10-23):
// watched areas, team alerts + per-member preferences, the shared dashboard and
// the department board. Every route is scoped by the Backend to the caller's own
// membership. When the feature is switched off (or the DB predates changelog
// 3.45) every route answers 503 `org_unavailable`; callers hide the feature.

export function fetchDepartmentAreas(token) {
  return departmentCall('/department/areas', token);
}

/**
 * Adds a watched area (admin). A place is `{kind:'place', placeKind, geoid}`;
 * a point is `{kind:'point', latitude, longitude, radiusKm}` (radiusKm <= 50).
 */
export function addDepartmentArea(token, body) {
  return departmentCall('/department/areas', token, 'POST', body);
}

export function deleteDepartmentArea(token, id) {
  return departmentCall(`/department/areas/${encodeURIComponent(id)}`, token, 'DELETE');
}

export function fetchDepartmentAlerts(token, { cursor, limit = 50 } = {}) {
  const p = new URLSearchParams();
  if (cursor) p.set('cursor', cursor);
  p.set('limit', String(Number(limit) | 0 || 50));
  return departmentCall(`/department/alerts?${p.toString()}`, token);
}

export function fetchDepartmentAlertPreferences(token) {
  return departmentCall('/department/alerts/preferences', token);
}

/** Per-member alert preferences. `email` opts into e-mail; `muted` silences the feed. */
export function updateDepartmentAlertPreferences(token, { email, muted }) {
  const body = { email: Boolean(email) };
  if (typeof muted === 'boolean') body.muted = muted;
  return departmentCall('/department/alerts/preferences', token, 'PUT', body);
}

export function fetchDepartmentDashboard(token) {
  return departmentCall('/department/dashboard', token);
}

export function fetchDepartmentBoard(token, { cursor, limit = 50 } = {}) {
  const p = new URLSearchParams();
  if (cursor) p.set('cursor', cursor);
  p.set('limit', String(Number(limit) | 0 || 50));
  return departmentCall(`/department/board?${p.toString()}`, token);
}

/**
 * Shares a note or a Guardian answer snapshot with the department. `kind`
 * is 'answer' or 'note'; `text` <= 8000, `note` <= 1000. For an answer,
 * `recordId` is the turn's record id (the Backend verifies it is the
 * caller's own turn).
 */
export function postDepartmentBoardPost(token, { kind, text, note, recordId }) {
  const body = { kind, text };
  if (typeof note === 'string' && note.length > 0) body.note = note;
  if (recordId) body.recordId = recordId;
  return departmentCall('/department/board', token, 'POST', body);
}

export function deleteDepartmentBoardPost(token, id) {
  return departmentCall(`/department/board/${encodeURIComponent(id)}`, token, 'DELETE');
}

// Department onboarding in one sitting (D3, workspace docs/guardian/09,
// 2026-10-23). Admin only. Questions are mined by the Backend from the
// department's own documents; answers create DRAFT terms / call signs that are
// approved in the vocabulary and structure sections. 503 onboarding_unavailable
// hides the section.

export function fetchDepartmentOnboarding(token) {
  return departmentCall('/department/onboarding', token);
}

export function answerOnboardingQuestion(token, questionKey, answer) {
  return departmentCall('/department/onboarding/answers', token, 'POST', { questionKey, answer });
}

export function dismissOnboardingQuestion(token, questionKey) {
  return departmentCall('/department/onboarding/dismiss', token, 'POST', { questionKey });
}

export function runKnowledgeCheck(token) {
  return departmentCall('/department/knowledge-check', token, 'POST', {});
}

// Department learning-loop suggestions (D4, workspace docs/guardian/10, rev 3.1,
// 2026-10-24). Admin only. The Backend computes proposals on read from the last
// 14 days of member signals (unknown tokens, no-rule questions, corrections,
// down-rated rules); answers/drafts create DRAFT terms approved in the
// vocabulary section. No member identity is ever returned. 503
// signals_unavailable hides the section.

export function fetchDepartmentSuggestions(token) {
  return departmentCall('/department/suggestions', token);
}

export function answerDepartmentSuggestion(token, key, answer) {
  return departmentCall('/department/suggestions/answer', token, 'POST', { key, ...answer });
}

export function draftDepartmentSuggestion(token, key) {
  return departmentCall('/department/suggestions/draft', token, 'POST', { key });
}

export function dismissDepartmentSuggestion(token, key) {
  return departmentCall('/department/suggestions/dismiss', token, 'POST', { key });
}
